/**
 * BrandContext — centralized product branding for the application.
 *
 * Usage:
 *   import { useBrand } from "../contexts/BrandContext";
 *   const { productName, companyName, tagline } = useBrand();
 *
 * Brand config is loaded ONCE at application bootstrap from /api/brand/config.
 * If the API is unavailable, safe fallback defaults are used so the app
 * never shows blank or "undefined" brand values.
 *
 * To rename the product: update environment variables on the server —
 * no source-code changes required.
 */
import { createContext, useContext, useState, useEffect } from "react";

// ─── Safe fallback defaults ──────────────────────────────────────────────────
// These are used only if the brand API is unreachable.
const FALLBACK_BRAND = {
  productName:        "Prashasta Events Intelligence Platform",
  productShortName:   "Prashasta",
  companyName:        "ARGOV Technologies",
  productDescriptor:  "Agentic Event Intelligence & Orchestration Platform",
  primaryTagline:     "One Event. One Live Truth. Every Party Connected.",
  secondaryTagline:   "",
  logoUrl:            "",
  logoMarkUrl:        "",
  faviconUrl:         "",
  supportEmail:       "",
  websiteUrl:         "",
  copyrightText:      `© ARGOV Technologies ${new Date().getFullYear()}`,
  environmentLabel:   "",
};

const BrandContext = createContext(FALLBACK_BRAND);

// ─── Provider ────────────────────────────────────────────────────────────────
export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(FALLBACK_BRAND);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;

    // Support URL-based preset for rebrand readiness testing (e.g. ?brandPreset=test).
    // This is a built-in testing mechanism — not a user-facing feature.
    const params = new URLSearchParams(window.location.search);
    const preset = params.get("brandPreset") || "";
    const endpoint = preset
      ? `${base}/api/brand/config?preset=${encodeURIComponent(preset)}`
      : `${base}/api/brand/config`;

    fetch(endpoint, { credentials: "include" })
      .then(res => (res.ok ? res.json() : Promise.reject()))
      .then(data => {
        setBrand({
          productName:       data.product_name        || FALLBACK_BRAND.productName,
          productShortName:  data.product_short_name  || FALLBACK_BRAND.productShortName,
          companyName:       data.company_name         || FALLBACK_BRAND.companyName,
          productDescriptor: data.product_descriptor   || FALLBACK_BRAND.productDescriptor,
          primaryTagline:    data.primary_tagline       || FALLBACK_BRAND.primaryTagline,
          secondaryTagline:  data.secondary_tagline     || FALLBACK_BRAND.secondaryTagline,
          logoUrl:           data.logo_url              || FALLBACK_BRAND.logoUrl,
          logoMarkUrl:       data.logo_mark_url         || FALLBACK_BRAND.logoMarkUrl,
          faviconUrl:        data.favicon_url           || FALLBACK_BRAND.faviconUrl,
          supportEmail:      data.support_email         || FALLBACK_BRAND.supportEmail,
          websiteUrl:        data.website_url           || FALLBACK_BRAND.websiteUrl,
          copyrightText:     data.copyright_text        || FALLBACK_BRAND.copyrightText,
          environmentLabel:  data.environment_label     || FALLBACK_BRAND.environmentLabel,
        });
      })
      .catch(() => {
        // Fallback already set — app continues with defaults.
      });
  }, []);

  return (
    <BrandContext.Provider value={brand}>
      {children}
    </BrandContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useBrand() {
  return useContext(BrandContext);
}
